import React, { useState } from 'react';
import {
    formatCustomerCurrency,
    formatCustomerDate,
    formatCustomerValue,
    getStatusColor,
} from './customerSubscriptionUtils';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const DetailItem = ({ label, value }) => (
    <div className="flex flex-row items-center gap-2 px-2 py-1 border border-gray-300">
        <p className="text-xs font-medium capitalize tracking-wide text-gray-400">{label}</p>
        <p className="mt-0 break-all text-xs font-semibold text-gray-800">{formatCustomerValue(value)}</p>
    </div>
);

const PlanDetails = ({ plan }) => {
    console.log("plan", plan);
    return (
    <div className="space-y-3 border-t border-gray-100 p-5">
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-50 p-4 md:grid-cols-3">
            <DetailItem label="Subscription ID" value={plan.subscription_id} />
            <DetailItem label="Plan Name" value={plan.subscription_name} />
            <DetailItem label="Product" value={plan.product_name} />
            <DetailItem label="Subscription Type" value={plan.subscription_type} />                                  
            <DetailItem label="Amount Paid" value={formatCustomerCurrency(plan.payable_amount, plan.currency)} />
            <DetailItem label="Currency" value={plan.currency} />  
            <DetailItem label="Billing Cycle" value={plan.billing_cycle} />
            <DetailItem label="Duration" value={`${plan.subscription_duration ?? "-"} ${plan.billing_cycle || ""}`} />
            <DetailItem label="Device Type" value={plan.device_type} />
        </div>        

        <div>
            <h3 className="mb-1 text-xs font-bold text-gray-900">Validity</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <DetailItem label="Start Date" value={formatCustomerDate(plan.start_date, true)} />
                <DetailItem label="End Date" value={formatCustomerDate(plan.end_date, true)} />
            </div>
        </div>

        <div>
            <h3 className="mb-1 text-xs font-bold text-gray-900">Payment References</h3>
            <div className="grid grid-cols-1 gap-1 lg:grid-cols-2">
                <DetailItem label="Order Id" value={plan.order_id} />
                <DetailItem label="Transaction Id" value={plan.transaction_id} />
                <DetailItem label="Razorpay Payment Id" value={plan.razorpay_payment_id} />
                <DetailItem label="Razorpay Sub Id" value={plan.razorpay_sub_id} />
            </div>
        </div>

        <div>
            <h3 className="mb-1 text-xs font-bold text-gray-900">Bundle Products</h3>
            <div className="mb-0 flex flex-wrap gap-2">
                {(plan.bundle_names || []).map((name) => (
                    <span key={name} className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                        {name}
                    </span>
                ))}
                {/* <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
                    Bundle ID: {plan.bundle_id || "-"}
                </span> */}
            </div>

            {(plan.status || "Active").toLowerCase() === "active" ? (
                <button type="button" disabled={true} className="mt-3 rounded-md bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50 disabled:pointer-events-none">Cancel Autopay</button>
            ) : null}
            {/* <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {(plan.bundle_products || []).map((product) => (
                    <div key={product.id} className="rounded-lg border border-gray-200 p-3">
                        <p className="text-xs font-semibold text-gray-900">{product.product_name || "-"}</p>
                        <p className="mt-1 text-xs text-gray-500">{product.domain_url || "-"}</p>
                        <p className="mt-2 text-xs text-gray-500">
                            ID: {product.id} · Locale: {product.locale || "-"} ({product.short_locale || "-"})
                        </p>
                    </div>
                ))}
            </div> */}
        </div>
    </div>
    );
};

const CustomerActivePlan = ({ activePlan }) => {
    const plans = Array.isArray(activePlan) ? activePlan : activePlan ? [activePlan] : [];
    const [openPlan, setOpenPlan] = useState(-1);

    return (
        <div className="h-full rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <div className="mb-2 border-b border-gray-100 pb-2">
                <h2 className="text-sm font-bold text-gray-900">Active Subscription Plans</h2>
                <p className="mt-0 text-xs text-gray-500">
                    {plans.length} active {plans.length === 1 ? "plan" : "plans"} with billing and payment information
                </p>
            </div>

            {plans.length === 0 ? (
                <p className="text-xs text-gray-500">No active subscription plan found.</p>
            ) : (
                <div className="space-y-3">
                    {plans.map((plan, index) => {
                        const isOpen = openPlan === index;
                        const status = plan.status || "Active";
                        return (
                            <div key={plan.subscription_id ?? index} className="overflow-hidden rounded-xl border border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setOpenPlan(isOpen ? -1 : index)}
                                    aria-expanded={isOpen}
                                    className="flex w-full items-center justify-between gap-4 bg-white px-5 py-2 text-left hover:bg-gray-50"
                                >
                                    <div>
                                        <p className="font-semibold text-gray-900 text-sm capitalize">{plan.subscription_name || "Subscription plan"} ({plan.subscription_type || "Subscription type"}) -  Plan expiry: {formatCustomerDate(plan.end_date, true)}</p>
                                        <p className="mt-1 text-xs text-gray-500">
                                            {plan.product_name || "-"} · {formatCustomerCurrency(plan.payable_amount, plan.currency)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(status)}`}>
                                            {status.toUpperCase()}
                                        </span>
                                        <span className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}><ChevronDownIcon width={20}  /></span>
                                    </div>
                                </button>
                                {isOpen ? <PlanDetails plan={plan} /> : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CustomerActivePlan;
